import { ARENA_RAKE_BPS_DIVISOR } from "@cede/types";
import Decimal from "decimal.js";

/** USDT precision on Arbitrum — arena rewards are credited/displayed to 6 dp. */
/** Ledger precision for every arena USDT amount. */
export const ARENA_USDT_DECIMALS = 6;

/** Breakdown of a competition prize pool once the platform rake is applied. */
export type ArenaRakeSplit = {
  /** The pool before rake — what participants pooled / the configured prize. */
  gross: Decimal;
  /** Rake taken by Cede Arena = gross − net. */
  rake: Decimal;
  /** What's distributed to the winner, rounded down to USDT precision. */
  net: Decimal;
};

/**
 * Splits a gross prize pool into the winner's net payout and Cede Arena's rake.
 *
 * `net` is rounded DOWN to USDT precision so Cede never overpays; the rounding
 * remainder falls into the rake (`rake = gross − net`). This is the single
 * source of truth for arena rake math — the payout path and every gross/net
 * display surface call it so they can never disagree.
 *
 * @param gross   The prize pool before rake (Decimal or decimal string).
 * @param rakeBps Rake in basis points (0–5000). `0`/nullish ⇒ no rake, net = gross.
 */
export function applyArenaRake(gross: Decimal.Value, rakeBps: number | null | undefined): ArenaRakeSplit {
  const grossDecimal = new Decimal(gross);
  const bps = rakeBps ?? 0;
  if (bps <= 0) {
    return { gross: grossDecimal, rake: new Decimal(0), net: grossDecimal };
  }
  const net = grossDecimal
    .times(ARENA_RAKE_BPS_DIVISOR - bps)
    .dividedBy(ARENA_RAKE_BPS_DIVISOR)
    .toDecimalPlaces(ARENA_USDT_DECIMALS, Decimal.ROUND_DOWN);
  return { gross: grossDecimal, rake: grossDecimal.minus(net), net };
}

/** Stacked breakdown of a prize pool once both the platform and creator rakes are applied. */
export type ArenaRakeStack = {
  /** The pool before any rake. */
  gross: Decimal;
  /** Cede Arena's cut (platform rake). */
  platformCut: Decimal;
  /** The competition creator's cut (creator rake). */
  creatorCut: Decimal;
  /** platformCut + creatorCut. */
  totalRake: Decimal;
  /** What's distributed to the winner. */
  net: Decimal;
};

/**
 * Splits a gross prize pool into the winner's net, Cede's platform cut, and the
 * creator's cut, with the two rakes STACKED ADDITIVELY on the gross (a $100 pool
 * at 5% platform + 10% creator ⇒ $5 + $10 rake, $85 net — not compounded).
 *
 * Rounding, all to USDT precision: the winner's `net` and the `creatorCut` are
 * each rounded DOWN (never overpaid); Cede's `platformCut` is the remainder
 * (`gross − net − creatorCut`) so it absorbs the rounding dust and the three
 * always sum EXACTLY to gross. `0`/nullish rakes drop out (net = gross).
 *
 * @param gross       The prize pool before rake.
 * @param platformBps Platform rake in basis points (0–5000).
 * @param creatorBps  Creator rake in basis points (0–2000).
 */
export function splitArenaRake(
  gross: Decimal.Value,
  platformBps: number | null | undefined,
  creatorBps: number | null | undefined,
): ArenaRakeStack {
  const grossDecimal = new Decimal(gross);
  const platform = platformBps ?? 0;
  const creator = creatorBps ?? 0;

  const net = grossDecimal
    .times(ARENA_RAKE_BPS_DIVISOR - platform - creator)
    .dividedBy(ARENA_RAKE_BPS_DIVISOR)
    .toDecimalPlaces(ARENA_USDT_DECIMALS, Decimal.ROUND_DOWN);
  const creatorCut = grossDecimal
    .times(creator)
    .dividedBy(ARENA_RAKE_BPS_DIVISOR)
    .toDecimalPlaces(ARENA_USDT_DECIMALS, Decimal.ROUND_DOWN);
  // Cede takes the remainder so gross = net + creatorCut + platformCut exactly
  // (platform absorbs the sub-micro rounding dust from the two floors above).
  const platformCut = grossDecimal.minus(net).minus(creatorCut);
  return { gross: grossDecimal, platformCut, creatorCut, totalRake: platformCut.plus(creatorCut), net };
}
